export type AssetLoadFailureKind =
  'chunk-loading' | 'css-preload' | 'module-loading' | 'mime-type';

const ASSET_LOAD_FAILURE_PATTERNS: Array<{
  kind: AssetLoadFailureKind;
  pattern: RegExp;
}> = [
  {
    kind: 'mime-type',
    pattern:
      /is not a valid JavaScript MIME type|failed to load module script/i,
  },
  {
    kind: 'module-loading',
    pattern:
      /Load failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i,
  },
  {
    kind: 'chunk-loading',
    pattern: /ChunkLoadError|Loading chunk/i,
  },
  {
    kind: 'css-preload',
    pattern: /Unable to preload CSS/i,
  },
];

const SCRIPT_ASSET_URL_PATTERN =
  /(?:https?:\/\/[^\s"'<>]+|\/[^\s"'<>]+)\.m?js(?:\?[^\s"'<>]*)?/gi;

const isSameOriginScriptAssetUrl = (url: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const parsedUrl = new URL(url, window.location.origin);
    return (
      parsedUrl.origin === window.location.origin &&
      /\.m?js$/i.test(parsedUrl.pathname)
    );
  } catch {
    return false;
  }
};

const extractScriptAssetUrls = (text: string): string[] => {
  const matches = text.matchAll(SCRIPT_ASSET_URL_PATTERN);
  return Array.from(matches, (match) => match[0]);
};

const isRecoverableTruncatedScriptError = (text: string): boolean => {
  if (
    !/\bSyntaxError\b/i.test(text) ||
    !/Unexpected end of script/i.test(text)
  ) {
    return false;
  }

  return extractScriptAssetUrls(text).some(isSameOriginScriptAssetUrl);
};

const appendErrorText = (
  value: unknown,
  messages: string[],
  seen: Set<object>
) => {
  if (typeof value === 'string') {
    const normalized = value.trim();
    if (normalized) {
      messages.push(normalized);
    }
    return;
  }

  if (value instanceof Error) {
    const errorRecord = value as Error & Record<string, unknown>;

    appendErrorText(value.name, messages, seen);
    appendErrorText(value.message, messages, seen);
    appendErrorText(errorRecord.filename, messages, seen);
    appendErrorText(errorRecord.fileName, messages, seen);
    appendErrorText(errorRecord.sourceURL, messages, seen);
    appendErrorText(value.stack, messages, seen);
    return;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      appendErrorText(item, messages, seen);
    }
    return;
  }

  if (typeof value !== 'object' || value === null) {
    return;
  }

  if (seen.has(value)) {
    return;
  }
  seen.add(value);

  const record = value as Record<string, unknown>;
  appendErrorText(record.message, messages, seen);
  appendErrorText(record.reason, messages, seen);
  appendErrorText(record.type, messages, seen);
  appendErrorText(record.filename, messages, seen);
  appendErrorText(record.fileName, messages, seen);
  appendErrorText(record.sourceURL, messages, seen);
  appendErrorText(record.detail, messages, seen);
  appendErrorText(record.payload, messages, seen);
  appendErrorText(record.error, messages, seen);
};

const extractAssetLoadFailureText = (error: unknown) => {
  const messages: string[] = [];
  appendErrorText(error, messages, new Set<object>());
  return messages.join(' ');
};

export const classifyAssetLoadFailure = (
  error: unknown
): { kind: AssetLoadFailureKind; text: string } | null => {
  const text = extractAssetLoadFailureText(error);
  if (!text) {
    return null;
  }

  const match = ASSET_LOAD_FAILURE_PATTERNS.find(({ pattern }) =>
    pattern.test(text)
  );

  if (!match) {
    if (isRecoverableTruncatedScriptError(text)) {
      return {
        kind: 'module-loading',
        text,
      };
    }

    return null;
  }

  return {
    kind: match.kind,
    text,
  };
};
